import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { useRef, useState } from 'react';

import { Invoice, InvoiceExpenseType, InvoiceProduct } from 'openapi';

import { Modal } from 'components/shared/Modal/Modal';
import { showSuccessToastMessage } from 'components/shared/Toast/Toast';

import { useTranslations } from 'context/TranslationContext';

import {
  LineItemSaveRow,
  useLineItemsSectionSave
} from 'hooks/useLineItemsSectionSave';

import { CONFIDENCE_STATUS_COLORS } from 'utils/constants/confidenceStatusColors';
import { ConfidenceLevel } from 'utils/enums/Confidence';
import { getLineItemsSummary } from 'utils/helpers/invoiceSectionSummaryHelpers';
import {
  formatEurAmount,
  formatEurWithSuffix,
  round2
} from 'utils/helpers/moneyHelper';

import {
  lineItemsDescriptionField,
  lineItemsEditableField,
  lineItemsQuantityField,
  lineItemsUnitPriceField,
  lineItemsFooterRow,
  lineItemsStatusBanner,
  lineItemsTableContainer,
  lineItemsTableHeaderCell,
  lineItemsTableHeaderRow,
  modalButtonsWrapper
} from 'styles/pages/InvoiceVerificationStyle';

import { CardSummary } from './CardSummary';
import { ConfidenceIcon } from './ConfidenceIcon';
import { InvoiceVerificationCard } from './InvoiceVerificationCard';

const DESCRIPTION_MAX_LENGTH = 255;

interface LineItemsCardProps {
  currentInvoice: Invoice;
  /**
   * Company expense types used both for the per-line dropdown and to resolve
   * effective per-line type names. Same source as the Document data card.
   */
  expenseTypeOptions?: InvoiceExpenseType[];
  /**
   * The document-level expense type (live form selection when available),
   * used as the effective per-line type for products without their own and
   * for the synthetic row.
   */
  documentExpenseTypeId?: number | string | null;
  /**
   * Called with the authoritative invoice returned by the products section
   * autosave so the parent can refresh its state.
   */
  onInvoiceUpdated?: (invoice: Invoice) => void;
  /**
   * Live Document amount to display on the synthetic row. Only provided when
   * the document currency is EUR, since the exchange rate is not available
   * client-side; when omitted (or null while the field is cleared) the row
   * falls back to the server-computed invoiceAmountEur.
   */
  liveDocumentAmountEur?: number | null;
  expanded?: boolean;
  onToggle?: () => void;
  onExpanded?: () => void;
  autoExpandReason?: string;
}

const COLUMN_HEADER_KEYS = [
  'labels.lineItemPositionColumn',
  'labels.description',
  'labels.lineItemQuantityColumn',
  'labels.unitPriceEur',
  'labels.totalEur',
  'labels.expenseType'
] as const;

/** Locally edited values for one product row, keyed by product id. */
interface RowEdits {
  description?: string;
  quantity?: string;
  unitPrice?: string;
  expenseTypeId?: number;
}

const formatThousands = (digits: string): string =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** Server value rendered into the unit price input: 2 decimals + thousands commas. */
const formatUnitPriceDisplay = (value: number): string => {
  const [integerPart, decimalPart] = value.toFixed(2).split('.');
  return `${formatThousands(integerPart)}.${decimalPart}`;
};

/** Reverts the display formatting (thousands commas) back to a number. */
const parseUnitPriceDisplay = (display: string): number =>
  Number(display.replace(/,/g, '')) || 0;

/**
 * Effective quantity of a row under the current edits: null when the row's
 * quantity is cleared (an empty field is a valid state that persists null).
 */
const getEffectiveQuantity = (
  row: LineItemRow,
  edit: RowEdits | undefined
): number | null => {
  if (edit?.quantity !== undefined) {
    return edit.quantity === '' ? null : Number(edit.quantity);
  }
  return row.quantity ?? null;
};

/**
 * Quantity accepts non-negative whole numbers (0 allowed): the input is cut
 * at the first decimal separator (so typing or pasting one is ignored) and
 * any non-digit is dropped. Clearing the field empties it - an empty quantity
 * is a valid state that persists null - while anything unusable (e.g. letters
 * only) rejects the edit and keeps the previously displayed value.
 */
const sanitizeQuantityInput = (raw: string, prev: string): string => {
  if (raw === '') {
    // The user cleared the field: let it empty out visually.
    return '';
  }
  const wholePart = raw
    .split(/[.,]/)[0]
    .replace(/[^0-9]/g, '')
    .replace(/^0+(?=\d)/, '');
  if (!wholePart) {
    // Nothing usable was entered - keep the previous value.
    return prev;
  }
  return wholePart;
};

/**
 * Unit price accepts non-negative numeric values with at most two decimal
 * places. '.' is the decimal separator, ',' is displayed as the thousands
 * separator. Invalid characters leave the previous value untouched; clearing
 * the field immediately shows 0.
 */
const sanitizeUnitPriceInput = (raw: string, prev: string): string => {
  if (raw === '') {
    return '0';
  }

  const cleaned = raw.replace(/[^0-9.,]/g, '');
  const hasSeparator = /[.,]/.test(cleaned);
  const [wholePart = '', ...decimalParts] = cleaned.split(/[.,]/);
  const decimalPart = decimalParts.join('').slice(0, 2);

  if (!wholePart && !decimalPart && !hasSeparator) {
    // Nothing usable was entered (e.g. letters only) - keep the previous value.
    return prev;
  }

  const integerPart = formatThousands(wholePart.replace(/^0+(?=\d)/, ''));
  if (hasSeparator) {
    return decimalPart ? `${integerPart}.${decimalPart}` : `${integerPart}.`;
  }
  return integerPart || '0';
};

interface LineItemRow {
  key: string | number;
  productId?: number;
  position: number;
  description?: string;
  quantity?: number;
  unitPriceEur: number | null;
  totalEur: number | null;
  /** Effective (display) type: the assigned type with the document-level fallback. */
  expenseTypeId: number | null;
  /**
   * The row's own assigned expense type without the document-level fallback:
   * null marks an untyped row (EPN-523 allows untyped rows until approval).
   */
  assignedExpenseTypeId: number | null;
  /** AI-extracted original expense type, absent when equal to the selection. */
  aiExpenseTypeId?: number;
  aiExpenseTypeName?: string;
  impliedRate: number | null;
  originalUnitPrice: number | null;
}

const mapProductToDisplayRow = (
  product: InvoiceProduct,
  index: number,
  fallbackExpenseTypeId: number | null
): LineItemRow => ({
  key: product.id ?? index,
  productId: product.id,
  position: index + 1,
  description: product.description,
  quantity: product.quantity,
  unitPriceEur: product.unitPriceEur ?? null,
  totalEur: product.totalEur ?? null,
  expenseTypeId: product.expenseTypeId ?? fallbackExpenseTypeId ?? null,
  assignedExpenseTypeId: product.expenseTypeId ?? null,
  aiExpenseTypeId: product.aiExpenseType?.id,
  aiExpenseTypeName: product.aiExpenseType?.name,
  impliedRate:
    product.unitPriceEur != null && product.unitPrice
      ? product.unitPriceEur / product.unitPrice
      : null,
  originalUnitPrice: product.unitPrice ?? null
});

/** A not-yet-persisted row added by the user (EPN-523): the server assigns
 * the real id on create; edits typed before the create response arrives are
 * kept on the pending row itself. */
interface PendingRow extends RowEdits {
  key: string;
}

// Add-line defaults (EPN-523 AC6/AC7): quantity 1, unit price 0.00; the
// description stays empty (placeholder) and no expense type is assigned (AC9).
const getPendingRowQuantity = (pending: PendingRow): number | null =>
  pending.quantity === undefined
    ? 1
    : pending.quantity === ''
      ? null
      : Number(pending.quantity);

const getPendingRowUnitPriceEur = (pending: PendingRow): number =>
  pending.unitPrice ? parseUnitPriceDisplay(pending.unitPrice) : 0;

const mapPendingToDisplayRow = (
  pending: PendingRow,
  index: number
): LineItemRow => {
  const quantity = getPendingRowQuantity(pending);
  const unitPriceEur = getPendingRowUnitPriceEur(pending);
  return {
    key: pending.key,
    position: index + 1,
    description: pending.description,
    quantity: quantity ?? undefined,
    unitPriceEur,
    totalEur: quantity == null ? null : round2(quantity * unitPriceEur),
    expenseTypeId: pending.expenseTypeId ?? null,
    assignedExpenseTypeId: pending.expenseTypeId ?? null,
    impliedRate: null,
    originalUnitPrice: null
  };
};

// A display-only stand-in row for documents without extracted products:
// position 1, quantity 1, no description, and the document amount as both
// unit price and total. Nothing is persisted or editable.
const createSyntheticDisplayRow = (totalEur: number | null): LineItemRow => ({
  key: 'synthetic',
  position: 1,
  quantity: 1,
  unitPriceEur: totalEur,
  totalEur: totalEur,
  expenseTypeId: null,
  assignedExpenseTypeId: null,
  impliedRate: null,
  originalUnitPrice: null
});

/** Edits typed on a pending (id-less) row that must move onto the product the
 * server created for it once the save response returns. */
const carryPendingRowEdits = (pending: PendingRow): RowEdits => {
  const carried: RowEdits = {};
  if (pending.description !== undefined) {
    carried.description = pending.description;
  }
  if (pending.quantity !== undefined) {
    carried.quantity = pending.quantity;
  }
  if (pending.unitPrice !== undefined) {
    carried.unitPrice = pending.unitPrice;
  }
  if (pending.expenseTypeId !== undefined) {
    carried.expenseTypeId = pending.expenseTypeId;
  }
  return carried;
};

/** Shared warning-banner look for the mismatch and untyped messages. */
const LineItemsStatusBanner = ({
  testId,
  children
}: {
  testId: string;
  children: React.ReactNode;
}) => (
  <Box data-testid={testId} sx={lineItemsStatusBanner}>
    <Box
      sx={{
        display: 'flex',
        flexShrink: 0,
        alignItems: 'flex-start'
      }}
    >
      <ConfidenceIcon level={ConfidenceLevel.MEDIUM} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ textAlign: 'left' }}>{children}</Typography>
    </Box>
  </Box>
);

export const LineItemsCard = ({
  currentInvoice,
  expenseTypeOptions = [],
  documentExpenseTypeId,
  onInvoiceUpdated,
  liveDocumentAmountEur,
  expanded,
  onToggle,
  onExpanded,
  autoExpandReason
}: LineItemsCardProps) => {
  const { translate } = useTranslations();
  const theme = useTheme();
  const products = currentInvoice.products ?? [];

  const { lineItemTotalEur, invoiceAmountEur, lineItemsMatchDocumentAmount } =
    currentInvoice;

  const [edits, setEdits] = useState<Record<number, RowEdits>>({});

  // Mirror of the edits state read by the autosave hook: updated synchronously
  // on every keystroke so a pending debounced save never sees stale values.
  const editsRef = useRef(edits);

  // EPN-523 row management. pendingRows are user-added rows not yet persisted
  // (create-on-click sends them as id-less entries); deletedIds are products
  // the user explicitly confirmed for deletion, omitted from the next PUT;
  // tableDismissed hides the whole table once the last row is deleted (AC17).
  const [pendingRows, setPendingRows] = useState<PendingRow[]>([]);
  const pendingRowsRef = useRef(pendingRows);
  const [deletedIds, setDeletedIds] = useState<Set<number>>(new Set());
  const deletedIdsRef = useRef(deletedIds);
  const [pendingDelete, setPendingDelete] = useState<LineItemRow | null>(null);
  const [tableDismissed, setTableDismissed] = useState(false);

  // EPN-607: "Set all to" bulk expense type. The selector holds a display
  // value only; a confirmed apply is staged into the row edits and pushed
  // through the serialized section save, so it can never race an autosave.
  const [bulkExpenseTypeId, setBulkExpenseTypeId] = useState<string | number>(
    ''
  );
  const [pendingBulkExpenseTypeId, setPendingBulkExpenseTypeId] = useState<
    number | null
  >(null);

  const pendingRowKeyCounter = useRef(0);

  const aliveProducts = products.filter(
    (product) => product.id == null || !deletedIds.has(product.id)
  );

  const hasProducts = aliveProducts.length > 0 || pendingRows.length > 0;

  const fallbackExpenseTypeId =
    documentExpenseTypeId != null && documentExpenseTypeId !== ''
      ? Number(documentExpenseTypeId)
      : null;

  const getSaveRows = (): LineItemSaveRow[] => {
    // desired-state PUT (EPN-523): every product row is sent, untyped rows
    // carry null expenseTypeId, and only explicitly confirmed deletions omit
    // an existing id (keystroke autosaves never delete)
    const rows = products
      .filter((product) => product.id != null)
      .filter((product) => !deletedIdsRef.current.has(product.id as number))
      .map<LineItemSaveRow>((product) => {
        const edit = editsRef.current[product.id as number] ?? {};

        const unitPriceEur = product.unitPriceEur ?? null;
        const typedUnitPriceEur = edit.unitPrice
          ? parseUnitPriceDisplay(edit.unitPrice)
          : (unitPriceEur ?? product.unitPrice ?? 0);
        // the row's own assigned type is sent - untyped rows stay untyped
        // until approval (EPN-523 AC14) instead of baking in the display
        // fallback, which would make the fallback permanent on save
        const expenseTypeId =
          edit.expenseTypeId ?? product.expenseTypeId ?? null;

        return {
          id: product.id as number,
          description: edit.description ?? product.description ?? '',
          // InvoiceProductPatch.quantity is number | null: an empty field
          // persists null (cleared), so the key must serialize as null rather
          // than being dropped as undefined.
          quantity:
            edit.quantity === undefined
              ? (product.quantity ?? null)
              : edit.quantity === ''
                ? null
                : Number(edit.quantity),
          unitPriceEur: typedUnitPriceEur,
          expenseTypeId,
          impliedRate:
            unitPriceEur != null && product.unitPrice
              ? unitPriceEur / product.unitPrice
              : null
        };
      });

    const pendingSaveRows = pendingRowsRef.current.map<LineItemSaveRow>(
      (pending) => ({
        tempKey: pending.key,
        description: pending.description ?? '',
        quantity: getPendingRowQuantity(pending),
        unitPriceEur: getPendingRowUnitPriceEur(pending),
        expenseTypeId: pending.expenseTypeId ?? null,
        impliedRate: null
      })
    );

    return [...rows, ...pendingSaveRows];
  };

  const handleInvoiceUpdated = (
    invoice: Invoice,
    savedRows: LineItemSaveRow[]
  ) => {
    onInvoiceUpdated?.(invoice);

    // Reconciliation is snapshot-derived: the id-less rows the save dispatched
    // are the ones created on the server (appended last), so rows added while
    // the request was in flight are neither matched nor dropped here.
    const dispatchedPending = savedRows.filter((row) => row.tempKey != null);
    const createdIds =
      dispatchedPending.length > 0
        ? (invoice.products ?? [])
            .slice(-dispatchedPending.length)
            .map((product) => product.id)
            .filter((id): id is number => id != null)
        : [];

    const dispatchedKeys = new Set(
      dispatchedPending.map((row) => row.tempKey as string)
    );
    const dispatchedPendingRows = pendingRowsRef.current.filter((pending) =>
      dispatchedKeys.has(pending.key)
    );
    const nextPending = pendingRowsRef.current.filter(
      (pending) => !dispatchedKeys.has(pending.key)
    );
    pendingRowsRef.current = nextPending;
    setPendingRows(nextPending);
    setDeletedIds(new Set());
    deletedIdsRef.current = new Set();
    if ((invoice.products ?? []).length > 0) {
      setTableDismissed(false);
    }

    // Retire the local edits the server has acknowledged; keep any field the
    // user changed after the save snapshot was taken. Edits typed on a
    // pending (id-less) row move onto the product created for it.
    setEdits((prev) => {
      const next = { ...prev };
      dispatchedPendingRows.forEach((pending, index) => {
        const createdId = createdIds[index];
        if (createdId == null) {
          return;
        }
        const carried = carryPendingRowEdits(pending);
        if (Object.keys(carried).length > 0) {
          next[createdId] = carried;
        }
      });
      savedRows.forEach((row) => {
        if (row.id == null) {
          return;
        }
        const edit = next[row.id];
        if (!edit) {
          return;
        }
        const remaining: RowEdits = {};
        if (
          edit.description !== undefined &&
          edit.description !== row.description
        ) {
          remaining.description = edit.description;
        }
        if (
          edit.quantity !== undefined &&
          (edit.quantity === ''
            ? row.quantity != null
            : Number(edit.quantity) !== row.quantity)
        ) {
          remaining.quantity = edit.quantity;
        }
        if (
          edit.unitPrice !== undefined &&
          parseUnitPriceDisplay(edit.unitPrice) !== row.unitPriceEur
        ) {
          remaining.unitPrice = edit.unitPrice;
        }
        if (
          edit.expenseTypeId !== undefined &&
          edit.expenseTypeId !== row.expenseTypeId
        ) {
          remaining.expenseTypeId = edit.expenseTypeId;
        }
        if (Object.keys(remaining).length > 0) {
          next[row.id] = remaining;
        } else {
          delete next[row.id];
        }
      });
      // Keep the ref mirror in sync with the retired state: otherwise the
      // next setEdit spreads the already-retired edits back into state and
      // resurrects them as phantom previews.
      editsRef.current = next;
      return next;
    });
  };

  const { scheduleSave, saveNow } = useLineItemsSectionSave({
    companyId: currentInvoice.companyId,
    invoiceId: currentInvoice.id,
    getRows: getSaveRows,
    onInvoiceUpdated: handleInvoiceUpdated
  });

  const setEdit = (productId: number, patch: RowEdits) => {
    const nextEdits = {
      ...editsRef.current,
      [productId]: { ...editsRef.current[productId], ...patch }
    };
    editsRef.current = nextEdits;
    setEdits(nextEdits);
    scheduleSave();
  };

  // EPN-523 AC2: Add line appends a row at the end of the table; the row is
  // persisted on click (create-on-click) so the server assigns its id and an
  // added-but-never-edited row still reaches approval.
  const handleAddLine = () => {
    pendingRowKeyCounter.current += 1;
    const pending: PendingRow = { key: `new-${pendingRowKeyCounter.current}` };
    const next = [...pendingRowsRef.current, pending];
    pendingRowsRef.current = next;
    setPendingRows(next);
    setTableDismissed(false);
    scheduleSave();
  };

  const setPendingEdit = (key: string, patch: RowEdits) => {
    const next = pendingRowsRef.current.map((pending) =>
      pending.key === key ? { ...pending, ...patch } : pending
    );
    pendingRowsRef.current = next;
    setPendingRows(next);
    scheduleSave();
  };

  // EPN-607: applying a type to every row stages it into the local edits and
  // saves immediately; the server applies the same capture-once AI-original
  // rule as the per-line edit, and the response retires the staged edits.
  const handleApplyBulkExpenseType = async () => {
    const selectedTypeId = pendingBulkExpenseTypeId;
    setPendingBulkExpenseTypeId(null);
    if (selectedTypeId == null) {
      return;
    }

    const affectedCount = displayRows.filter(
      (row) => !isSyntheticRow(row)
    ).length;
    const selectedName = resolveExpenseTypeName(selectedTypeId) ?? '';

    const nextEdits = { ...editsRef.current };
    products.forEach((product) => {
      if (product.id == null || deletedIdsRef.current.has(product.id)) {
        return;
      }
      nextEdits[product.id] = {
        ...(nextEdits[product.id] ?? {}),
        expenseTypeId: selectedTypeId
      };
    });
    editsRef.current = nextEdits;
    setEdits(nextEdits);

    const nextPending = pendingRowsRef.current.map((pending) => ({
      ...pending,
      expenseTypeId: selectedTypeId
    }));
    pendingRowsRef.current = nextPending;
    setPendingRows(nextPending);

    // AC14: the selector is cleared once the apply is confirmed
    setBulkExpenseTypeId('');

    const saved = await saveNow();
    if (saved) {
      showSuccessToastMessage(
        translate(
          affectedCount === 1
            ? 'messages.expenseTypeAppliedToAllSingle'
            : 'messages.expenseTypeAppliedToAll',
          {
            name: selectedName,
            count: String(affectedCount)
          }
        )
      );
    }
  };

  const requestDeleteRow = (row: LineItemRow) => {
    setPendingDelete(row);
  };

  const handleCancelDelete = () => {
    setPendingDelete(null);
  };

  const handleConfirmDelete = () => {
    const row = pendingDelete;
    setPendingDelete(null);
    if (!row) {
      return;
    }

    if (row.productId == null) {
      // a pending (not yet persisted) row: dropping it needs no server call,
      // but if its create is still in flight the follow-up save must be able
      // to send an empty list so the server never keeps the dropped row
      const next = pendingRowsRef.current.filter(
        (pending) => pending.key !== row.key
      );
      pendingRowsRef.current = next;
      setPendingRows(next);
      scheduleSave({ allowEmpty: true });
      return;
    }

    // confirmed deletion: the next PUT omits the id, which deletes it on the
    // server; normal keystroke autosaves never omit existing ids
    const nextDeleted = new Set(deletedIdsRef.current);
    nextDeleted.add(row.productId);
    deletedIdsRef.current = nextDeleted;
    setDeletedIds(nextDeleted);
    const nextEdits = { ...editsRef.current };
    delete nextEdits[row.productId];
    editsRef.current = nextEdits;
    setEdits(nextEdits);

    const remainingRows =
      products.filter(
        (product) =>
          product.id != null && !nextDeleted.has(product.id as number)
      ).length + pendingRowsRef.current.length;
    if (remainingRows === 0) {
      // AC17: deleting the last row removes the whole table
      setTableDismissed(true);
    }
    scheduleSave({ allowEmpty: true });
  };

  const syntheticTotalEur =
    liveDocumentAmountEur === undefined
      ? (invoiceAmountEur ?? null)
      : liveDocumentAmountEur;

  // EPN-521: a document without extracted products shows one display-only
  // synthetic row. EPN-523 AC17: after the user deletes the last row the
  // whole table disappears (tableDismissed) instead.
  const showSynthetic =
    aliveProducts.length === 0 && pendingRows.length === 0 && !tableDismissed;
  const tableHidden = !showSynthetic && !hasProducts;

  const displayRows = showSynthetic
    ? [createSyntheticDisplayRow(syntheticTotalEur)]
    : [
        ...aliveProducts.map((product, index) =>
          mapProductToDisplayRow(product, index, fallbackExpenseTypeId)
        ),
        ...pendingRows.map((pending, index) =>
          mapPendingToDisplayRow(pending, aliveProducts.length + index)
        )
      ];

  const effectiveLineItemTotalEur = hasProducts
    ? (lineItemTotalEur ?? null)
    : syntheticTotalEur;

  // Live preview: while any quantity/unit price edit is unsaved, the aggregated
  // total is summed locally; the authoritative server value wins after each
  // save. Match judgment stays server-side except for the typing preview.
  // Unpersisted rows (just added) always participate in the local aggregate.
  const hasAmountEdits =
    (hasProducts &&
      aliveProducts.some((product) => {
        const edit = product.id != null ? edits[product.id] : undefined;
        return edit?.quantity !== undefined || edit?.unitPrice !== undefined;
      })) ||
    pendingRows.length > 0;

  // Live preview of each row's total while editing: Qty x Unit price in the
  // row's displayed basis. Unedited rows keep the authoritative server total;
  // a row without an EUR basis or with a cleared quantity has no total. The
  // aggregate below and the per-row cells both read from this one pass.
  const previewRowTotalsEur = displayRows.map((row) => {
    const edit = row.productId ? edits[row.productId] : undefined;
    const hasRowAmountEdits =
      edit?.quantity !== undefined || edit?.unitPrice !== undefined;
    if (!hasRowAmountEdits) {
      return row.totalEur ?? null;
    }
    const quantity = getEffectiveQuantity(row, edit);
    const unitPriceEur = edit?.unitPrice
      ? parseUnitPriceDisplay(edit.unitPrice)
      : row.unitPriceEur;
    return quantity == null || unitPriceEur == null
      ? null
      : round2(quantity * unitPriceEur);
  });

  let previewLineItemTotalEur = effectiveLineItemTotalEur;
  if (hasAmountEdits) {
    let aggregated: number | null = 0;
    displayRows.forEach((row, index) => {
      const edit = row.productId ? edits[row.productId] : undefined;
      // A cleared quantity contributes no total (the server's null handling):
      // the row is excluded from the aggregate, unlike a rate-less row whose
      // contribution is genuinely uncomputable.
      if (getEffectiveQuantity(row, edit) == null) {
        return;
      }
      const contribution = previewRowTotalsEur[index];
      if (contribution == null) {
        aggregated = null;
      } else if (aggregated != null) {
        aggregated += contribution;
      }
    });
    previewLineItemTotalEur = aggregated;
  }

  // While amount edits are unsaved and the preview aggregate is computable,
  // the displayed match state (difference row, banner, icons) follows the
  // local preview so it updates with each keystroke; the server-authoritative
  // flag decides once the autosave response applies. A null verdict falls
  // back to the server flag, e.g. when the aggregate cannot be computed.
  const previewMismatch =
    hasAmountEdits &&
    previewLineItemTotalEur != null &&
    invoiceAmountEur != null
      ? previewLineItemTotalEur.toFixed(2) !== invoiceAmountEur.toFixed(2)
      : null;

  const isMatch = hasProducts
    ? previewMismatch === null
      ? lineItemsMatchDocumentAmount === true
      : !previewMismatch
    : syntheticTotalEur != null;
  const isMismatch = hasProducts
    ? (previewMismatch ?? lineItemsMatchDocumentAmount === false)
    : false;

  // The preview total wins when computable; an uncomputable aggregate (e.g.
  // every row is rate-less) falls back to the server total so the difference
  // stays server-driven instead of showing a wrong number.
  const differenceTotalEur =
    previewLineItemTotalEur ?? effectiveLineItemTotalEur;
  const difference =
    isMismatch && invoiceAmountEur != null && differenceTotalEur != null
      ? Math.abs(invoiceAmountEur - differenceTotalEur)
      : null;

  // EPN-523: the summaries count only explicitly assigned types - untyped
  // rows are reported separately as "+ untyped" (AC24) instead of blending
  // into the document-level display fallback.
  const resolveExpenseTypeName = (expenseTypeId: number | null) =>
    expenseTypeOptions.find((option) => option.id === expenseTypeId)?.name;

  const isSyntheticRow = (row: LineItemRow) =>
    row.productId == null && row.key === 'synthetic';

  const getRowAssignedExpenseTypeId = (row: LineItemRow): number | null => {
    const editedType = row.productId
      ? edits[row.productId]?.expenseTypeId
      : undefined;
    return editedType ?? row.assignedExpenseTypeId ?? null;
  };

  const expenseTypeNames = displayRows
    .filter((row) => !isSyntheticRow(row))
    .map((row) => resolveExpenseTypeName(getRowAssignedExpenseTypeId(row)))
    .filter((name): name is string => Boolean(name));

  const untypedCount = displayRows.filter(
    (row) => !isSyntheticRow(row) && getRowAssignedExpenseTypeId(row) == null
  ).length;

  const summary = getLineItemsSummary(
    {
      lineItemCount: displayRows.length,
      lineItemTotalEur: previewLineItemTotalEur,
      expenseTypeNames,
      untypedCount,
      needsReview: isMismatch
    },
    translate
  );

  const footerValueSx = { fontWeight: 600 } as const;
  const mismatchValueSx = {
    fontWeight: 600,
    color: CONFIDENCE_STATUS_COLORS.needsReview.text
  } as const;

  return (
    <InvoiceVerificationCard
      title={translate('labels.lineItems')}
      defaultExpanded={false}
      expanded={expanded}
      onToggle={onToggle}
      onExpanded={onExpanded}
      autoExpandReason={autoExpandReason}
      summary={summary}
      confidenceLevel={isMismatch ? ConfidenceLevel.MEDIUM : undefined}
      headerStartAdornment={
        isMatch ? (
          <ConfidenceIcon level={ConfidenceLevel.HIGH} />
        ) : isMismatch ? (
          <ConfidenceIcon level={ConfidenceLevel.MEDIUM} />
        ) : undefined
      }
    >
      <Box
        data-testid="line-items-body"
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <CardSummary
          summary={summary}
          testId="line-items-summary"
          sx={{ marginBottom: 1 }}
        />
        <Box
          data-testid="line-items-toolbar"
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 1,
            marginBottom: 1
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#9ca3af',
              whiteSpace: 'nowrap'
            }}
          >
            {translate('labels.setAllTo')}
          </Typography>
          <Select
            size="small"
            variant="outlined"
            data-testid="line-items-set-all-to"
            sx={{
              minWidth: 220,
              textAlign: 'left',
              // the small outlined Select is 40px tall; trim the vertical
              // padding so it renders at the small button height (~31px)
              '& .MuiSelect-select': {
                paddingTop: '4px',
                paddingBottom: '4px'
              }
            }}
            slotProps={{
              input: {
                'aria-label': translate('labels.setAllToExpenseType')
              }
            }}
            value={bulkExpenseTypeId}
            renderValue={(selected) => {
              const option = expenseTypeOptions.find(
                (expenseType) => String(expenseType.id) === String(selected)
              );
              return option?.name ?? '';
            }}
            onChange={(event) =>
              setBulkExpenseTypeId(event.target.value as string)
            }
          >
            {expenseTypeOptions.map(
              (expenseType: InvoiceExpenseType) =>
                expenseType.isActive && (
                  <MenuItem key={expenseType.id} value={expenseType.id}>
                    {expenseType.name}
                  </MenuItem>
                )
            )}
          </Select>
          <Button
            data-testid="line-items-apply-expense-type"
            variant="outlined"
            size="small"
            disabled={!bulkExpenseTypeId}
            onClick={() =>
              setPendingBulkExpenseTypeId(Number(bulkExpenseTypeId))
            }
          >
            {translate('buttons.apply')}
          </Button>
          <Button
            data-testid="line-items-add-button"
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddLine}
          >
            {translate('labels.addLine')}
          </Button>
        </Box>
        {!tableHidden && (
          <>
            <Box
              data-testid="line-items-table-container"
              sx={lineItemsTableContainer}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={lineItemsTableHeaderRow}>
                    {COLUMN_HEADER_KEYS.map((headerKey) => (
                      <TableCell key={headerKey} sx={lineItemsTableHeaderCell}>
                        {translate(headerKey)}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={lineItemsTableHeaderCell}
                      aria-hidden="true"
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayRows.map((row, index) => {
                    const edit = row.productId
                      ? (edits[row.productId] ?? {})
                      : {};
                    const isSynthetic = row.key === 'synthetic';
                    const isPending = !isSynthetic && row.productId == null;
                    const updateRow = (patch: RowEdits) =>
                      isPending
                        ? setPendingEdit(row.key as string, patch)
                        : setEdit(row.productId as number, patch);

                    const descriptionValue =
                      edit.description ?? row.description ?? '';
                    const quantityValue =
                      edit.quantity ??
                      (row.quantity == null ? '' : String(row.quantity));
                    const unitPriceDisplay =
                      edit.unitPrice ??
                      formatUnitPriceDisplay(
                        row.unitPriceEur ?? row.originalUnitPrice ?? 0
                      );

                    return (
                      <TableRow key={row.key} hover>
                        <TableCell>{row.position}</TableCell>
                        <TableCell>
                          {isSynthetic ? (
                            (row.description ?? '')
                          ) : (
                            <TextField
                              size="small"
                              variant="outlined"
                              sx={lineItemsDescriptionField}
                              slotProps={{
                                htmlInput: {
                                  'aria-label': translate('labels.description'),
                                  placeholder: translate('labels.description')
                                }
                              }}
                              value={descriptionValue}
                              onChange={(event) =>
                                updateRow({
                                  description: event.target.value.slice(
                                    0,
                                    DESCRIPTION_MAX_LENGTH
                                  )
                                })
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {isSynthetic ? (
                            row.quantity
                          ) : (
                            <TextField
                              size="small"
                              variant="outlined"
                              sx={lineItemsQuantityField}
                              slotProps={{
                                htmlInput: {
                                  'aria-label': translate('labels.quantity'),
                                  inputMode: 'numeric'
                                }
                              }}
                              value={quantityValue}
                              onChange={(event) => {
                                const sanitized = sanitizeQuantityInput(
                                  event.target.value,
                                  quantityValue
                                );
                                if (sanitized !== quantityValue) {
                                  updateRow({ quantity: sanitized });
                                }
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {isSynthetic ? (
                            formatEurAmount(row.unitPriceEur)
                          ) : (
                            <TextField
                              size="small"
                              variant="outlined"
                              sx={lineItemsUnitPriceField}
                              slotProps={{
                                htmlInput: {
                                  'aria-label': translate(
                                    'labels.unitPriceEur'
                                  ),
                                  inputMode: 'decimal'
                                }
                              }}
                              value={unitPriceDisplay}
                              onChange={(event) =>
                                updateRow({
                                  unitPrice: sanitizeUnitPriceInput(
                                    event.target.value,
                                    unitPriceDisplay
                                  )
                                })
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {previewRowTotalsEur[index] != null && (
                            <Typography
                              sx={{ fontWeight: 600, fontSize: '1rem' }}
                            >
                              {formatEurAmount(previewRowTotalsEur[index])}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {isSynthetic ? (
                            (expenseTypeOptions.find(
                              (expenseType) =>
                                String(expenseType.id) ===
                                String(documentExpenseTypeId)
                            )?.name ?? '')
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0
                              }}
                            >
                              <Select
                                size="small"
                                variant="outlined"
                                sx={lineItemsEditableField}
                                slotProps={{
                                  input: {
                                    'aria-label':
                                      translate('labels.expenseType')
                                  }
                                }}
                                value={
                                  edit.expenseTypeId ?? row.expenseTypeId ?? ''
                                }
                                renderValue={(selected) => {
                                  const option = expenseTypeOptions.find(
                                    (expenseType) =>
                                      String(expenseType.id) ===
                                      String(selected)
                                  );
                                  return option?.name ?? '';
                                }}
                                onChange={(event) =>
                                  updateRow({
                                    expenseTypeId: Number(event.target.value)
                                  })
                                }
                              >
                                {expenseTypeOptions.map(
                                  (expenseType: InvoiceExpenseType) =>
                                    expenseType.isActive && (
                                      <MenuItem
                                        key={expenseType.id}
                                        value={expenseType.id}
                                      >
                                        {expenseType.name}
                                      </MenuItem>
                                    )
                                )}
                              </Select>
                              {row.aiExpenseTypeId !== undefined &&
                                row.aiExpenseTypeId !==
                                  (edit.expenseTypeId ?? row.expenseTypeId) && (
                                  <Typography
                                    data-testid="ai-expense-type"
                                    variant="caption"
                                    sx={{ color: '#9ca3af' }}
                                  >
                                    {translate('labels.aiExpenseType', {
                                      name: row.aiExpenseTypeName ?? ''
                                    })}
                                  </Typography>
                                )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {!isSynthetic && (
                            <IconButton
                              size="small"
                              data-testid="line-items-delete"
                              aria-label={translate('buttons.delete')}
                              onClick={() => requestDeleteRow(row)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            <Box
              data-testid="line-items-footer"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                marginTop: 1
              }}
            >
              <Box sx={lineItemsFooterRow}>
                <Typography>{translate('labels.lineItemTotal')}</Typography>
                <Typography
                  data-testid="line-items-line-item-total-value"
                  sx={isMismatch ? mismatchValueSx : footerValueSx}
                >
                  {formatEurWithSuffix(previewLineItemTotalEur)}
                </Typography>
              </Box>
              <Box sx={lineItemsFooterRow}>
                <Typography>{translate('labels.documentAmount')}</Typography>
                <Typography
                  data-testid="line-items-document-amount-value"
                  sx={footerValueSx}
                >
                  {formatEurWithSuffix(
                    hasProducts ? invoiceAmountEur : syntheticTotalEur
                  )}
                </Typography>
              </Box>
              {isMismatch && (
                <Box
                  data-testid="line-items-difference-row"
                  sx={lineItemsFooterRow}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ConfidenceIcon level={ConfidenceLevel.MEDIUM} />
                    <Typography
                      sx={{ color: CONFIDENCE_STATUS_COLORS.needsReview.text }}
                    >
                      {translate('labels.difference')}
                    </Typography>
                  </Box>
                  <Typography sx={mismatchValueSx}>
                    {formatEurWithSuffix(difference)}
                  </Typography>
                </Box>
              )}
              {isMatch && (
                <Box
                  data-testid="line-items-match-message"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <ConfidenceIcon level={ConfidenceLevel.HIGH} />
                  <Typography sx={{ color: theme.palette.success.main }}>
                    {translate('messages.lineItemsMatch')}
                  </Typography>
                </Box>
              )}
              {isMismatch && (
                <LineItemsStatusBanner testId="line-items-warning-banner">
                  {translate('messages.lineItemsMismatch')}
                </LineItemsStatusBanner>
              )}
              {untypedCount > 0 && (
                <LineItemsStatusBanner testId="line-items-untyped-banner">
                  {translate('messages.lineItemsUntyped')}
                </LineItemsStatusBanner>
              )}
            </Box>
          </>
        )}
        <Modal
          headerTitle={translate('messages.deleteLineItemTitle')}
          isOpen={pendingDelete !== null}
          hide={handleCancelDelete}
          maxWidth="sm"
        >
          <Typography variant="body1" mb={2}>
            {translate(
              displayRows.length === 1
                ? 'messages.deleteLastLineItemConfirmation'
                : 'messages.deleteLineItemConfirmation'
            )}
          </Typography>
          <Box sx={modalButtonsWrapper}>
            <Button
              variant="outlined"
              onClick={handleCancelDelete}
              data-testid="line-items-delete-cancel"
            >
              {translate('buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDelete}
              data-testid="line-items-delete-confirm"
            >
              {translate('buttons.delete')}
            </Button>
          </Box>
        </Modal>
        <Modal
          headerTitle={translate('messages.applyExpenseTypeTitle')}
          isOpen={pendingBulkExpenseTypeId !== null}
          hide={() => setPendingBulkExpenseTypeId(null)}
          maxWidth="sm"
        >
          <Typography variant="body1" mb={2}>
            {translate('messages.applyExpenseTypeToAllStart')}{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {resolveExpenseTypeName(pendingBulkExpenseTypeId) ?? ''}
            </Box>{' '}
            {translate('messages.applyExpenseTypeToAllEnd')}
          </Typography>
          <Box sx={modalButtonsWrapper}>
            <Button
              variant="outlined"
              onClick={() => setPendingBulkExpenseTypeId(null)}
              data-testid="line-items-bulk-cancel"
            >
              {translate('buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyBulkExpenseType}
              data-testid="line-items-bulk-apply"
            >
              {translate('buttons.apply')}
            </Button>
          </Box>
        </Modal>
      </Box>
    </InvoiceVerificationCard>
  );
};
